// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import HeaderPanel from "@/UI/components/HeaderPanel";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** A logo or a portrait, beside the name rather than above it. */
    avatar?: ReactNode;
    title: string;
    subtitle?: string;
    /** Derived values that identify the record: access, period, city. Chips, never controls. */
    chips?: ReactNode;
    /** The one action this record is usually opened to take — confirming a payment, editing it. */
    primary?: { label: string; onPress: () => void };
    /** Everything else it can have done to it, behind one button rather than six on the header. */
    onMore?: (anchor: DOMRect) => void;
    /** The actions bar. It is the panel's last band rather than a separate card below it. */
    children?: ReactNode;
};

/**
 * The top of a record screen: who this is, in the head of a `HeaderPanel`, with the bar of tabs and
 * actions as that panel's last band.
 *
 * The figures stay out of it, beside whatever they count in the content below. A header's job is to
 * say what you are looking at; a column of counts pushes that job down the screen and makes the real
 * content start below the fold.
 *
 * On a phone the head restacks: the back button stays beside the title, the chips drop beneath it,
 * and the actions take their own row where a thumb can reach them.
 */
export default function RecordPanel({ avatar, title, subtitle, chips, primary, onMore, children }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();

    return (
        <HeaderPanel
            head={
                <header className="record-panel__head">
                    {/* A real step back, not a fabricated "up" link — one of those adds a second
                        history entry, and pressing back twice then lands where you started. */}
                    <button type="button" className="record-panel__back" onClick={() => navigate(-1)} aria-label={t.common.back}>
                        <Icon name="chevron" size={16} />
                    </button>

                    {avatar && <div className="record-panel__avatar">{avatar}</div>}

                    <div className="record-panel__identity">
                        <h1 className="record-panel__title">{title}</h1>
                        {subtitle && <span className="record-panel__subtitle">{subtitle}</span>}
                    </div>

                    {chips && <div className="record-panel__chips">{chips}</div>}

                    <div className="record-panel__actions">
                        {primary && (
                            <button type="button" className="record-panel__primary" onClick={primary.onPress}>
                                <span>{primary.label}</span>
                            </button>
                        )}
                        {onMore && (
                            <button
                                type="button"
                                className="record-panel__ghost"
                                onClick={(event) => onMore(event.currentTarget.getBoundingClientRect())}
                                aria-label={t.common.more}
                                title={t.common.more}
                            >
                                <Icon name="more" size={16} />
                            </button>
                        )}
                    </div>
                </header>
            }
        >
            {children}
        </HeaderPanel>
    );
}
